<template>
  <v-dialog
    v-model="isDialogVisible"
    transition="dialog-bottom-transition"
    width="auto"
  >
    <template #activator="{ props }">
      <v-btn variant="text" icon="settings" v-bind="props" class="d-flex" />
    </template>
    <template #default="{ isActive }">
      <v-card class="pa-5">
        <v-card-title class="text-h5">Chart Settings</v-card-title>
        <v-row>
          <DisplayOptions
            :show-dots="showDots"
            :autoscaling="autoscaling"
            :min-y="minY"
            :max-y="maxY"
            @change-show-dots="changeShowDots"
            @change-scaling="changeScaling"
          />
          <v-col class="sensor-pane">
            <v-card-subtitle>Dataset Options</v-card-subtitle>
            <v-card-text>
              <v-select
                v-model="selectedItem"
                label="Select"
                :items="selectItems"
                item-title="title"
                item-value="key"
                variant="underlined"
                persistent-hint
                return-object
                single-line
              />
              <div>
                <v-row>
                  <v-switch
                    v-model="sensorStates[selectedItem.code].enabled"
                    label="Enabled"
                    :color="sensorStates[selectedItem.code].color"
                    class="pl-5"
                  />
                  <v-tooltip text="Default Color" location="start">
                    <template #activator="{ props }">
                      <v-btn
                        v-bind="props"
                        variant="text"
                        icon="restore"
                        :color="sensorSpecs[selectedItem.code].defaultColor"
                        @click="
                          sensorStates[selectedItem.code].color =
                            sensorSpecs[selectedItem.code].defaultColor
                        "
                      />
                    </template>
                  </v-tooltip>
                </v-row>
                <v-color-picker
                  v-model="sensorStates[selectedItem.code].color"
                />
              </div>
            </v-card-text>
            <v-card-actions class="justify-end">
              <v-btn variant="text" @click="isActive.value = false">
                Close
              </v-btn>
            </v-card-actions>
          </v-col>
        </v-row>
      </v-card>
    </template>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, onUpdated, computed } from "vue";
import { allSensorCodes, sensorSpecs } from "@/lib/sensors";
import { useSensorNames } from "@/composables/useSensorNames";
import { SensorCode } from "@/types/sensor/SensorCode";
import { SensorStates } from "@/types/sensor/SensorStates";
import DisplayOptions from "./DisplayOptions.vue";

const { getSensorName } = useSensorNames();

const props = defineProps<{
  sensorStates: SensorStates;
  showDots: boolean;
  autoscaling: boolean;
  minY: number;
  maxY: number;
}>();

const emit = defineEmits(["changeShowDots", "changeScaling", "chartUpdate"]);

// pass down
const showDots = ref<boolean>(props.showDots);
const autoscaling = ref<boolean>(props.autoscaling);
const minY = ref<number>(props.minY);
const maxY = ref<number>(props.maxY);

const isDialogVisible = ref(false);

const selectItems = computed(() =>
  allSensorCodes.reduce(
    (acc, sensorCode: SensorCode) => {
      acc.push({
        code: sensorCode,
        title: getSensorName(sensorCode),
        key: sensorSpecs[sensorCode].baseStorageKey,
      });
      return acc;
    },
    [] as { code: SensorCode; title: string; key: string }[],
  ),
);
const selectedItem = ref(selectItems.value[0]);

function changeShowDots(newVal: boolean) {
  // pass up
  emit("changeShowDots", newVal);
}

function changeScaling(
  newAutoscaling: boolean,
  newMinY: number,
  newMaxY: number,
) {
  // pass up
  emit("changeScaling", newAutoscaling, newMinY, newMaxY);
}

onUpdated(() => {
  showDots.value = props.showDots;
  autoscaling.value = props.autoscaling;
  minY.value = props.minY;
  maxY.value = props.maxY;
});
</script>
